import React, { useMemo, useState } from "react";
import {
  Calendar,
  Download,
  FileSpreadsheet,
  Info,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { formatDate } from "../../utils/formatters";
import { getHoldingMe } from "../../api/holding";
import { generateHoldingPackagesReport } from "../../api/inform";
import { showToast } from "../../utils/showToast";
import { apiBodyMessage } from "../../utils/apiErrorMessage";
import DateRangeFilter, { rangeFromPreset } from "../../components/dateRangeFilter/DateRangeFilter";
import { useUrlState } from "../../hooks/useUrlState";

const ymd = (d) => d.toISOString().slice(0, 10);

// Presets ofrecidos por el filtro (claves canónicas de DateRangeFilter).
const INFORM_PRESETS = [
  { key: "today", quick: true },
  { key: "yesterday", quick: true },
  { key: "7d", quick: true },
  // "Este mes hasta hoy" (no el mes completo) para no exportar un endDate futuro.
  { key: "thisMonthToToday", quick: true },
  { key: "lastMonth", quick: true },
];
const PRESET_KEYS = INFORM_PRESETS.map((p) => p.key);

// Default histórico de la vista: "último mes" rodante (hoy − 1 mes → hoy).
function defaultMonthRange(today) {
  const t = new Date(`${today}T00:00:00`);
  const d = new Date(t);
  d.setMonth(d.getMonth() - 1);
  return { desde: ymd(d), hasta: today };
}

// Deriva {desde, hasta} (YMD) desde el preset (+ rango custom) de la URL.
// Sin ?preset en la URL ⇒ "ultimoMes" ⇒ rango idéntico al comportamiento previo.
function deriveRange(preset, from, to, today) {
  if (preset === "custom") {
    return { desde: from || "", hasta: to || "" };
  }
  if (PRESET_KEYS.includes(preset)) {
    const [s, e] = rangeFromPreset(preset);
    return { desde: s.format("YYYY-MM-DD"), hasta: e.format("YYYY-MM-DD") };
  }
  return defaultMonthRange(today);
}

/**
 * "Crear informe" del perfil holding admin.
 *
 * Hoy hay un único informe disponible: el consolidado Excel de paquetes
 * de todas las empresas hijas en un rango de fechas. El endpoint server-side
 * filtra por `holding.companies[]` usando el `:holdingId` de la URL.
 */
export default function HoldingInform() {
  const { t } = useTranslation();
  const holding = getHoldingMe();
  const today = useMemo(() => ymd(new Date()), []);

  // Estado de filtro persistido en la URL. Default "ultimoMes" ⇒ sin params en
  // la URL el rango es idéntico al histórico (hoy − 1 mes → hoy).
  const [preset, setPreset] = useUrlState("preset", "ultimoMes");
  const [customFrom, setCustomFrom] = useUrlState("from", "");
  const [customTo, setCustomTo] = useUrlState("to", "");
  const [loading, setLoading] = useState(false);

  const range = useMemo(
    () => deriveRange(preset, customFrom, customTo, today),
    [preset, customFrom, customTo, today],
  );

  // value del DateRangeFilter: un chip conocido, o "custom" para el default
  // rodante / un rango personalizado (ambos se muestran con fechas concretas).
  const filterValue = PRESET_KEYS.includes(preset) ? preset : "custom";

  const handleRangeChange = (startYMD, endYMD, p) => {
    if (p === "custom") {
      setPreset("custom");
      setCustomFrom(startYMD);
      setCustomTo(endYMD);
    } else {
      setPreset(p);
      setCustomFrom("");
      setCustomTo("");
    }
  };

  const rangeValid = !!range?.desde && !!range?.hasta && range.desde <= range.hasta;

  const rangeLabel = useMemo(() => {
    if (!range?.desde || !range?.hasta) return "—";
    if (range.desde === range.hasta) return formatDate(`${range.desde}T00:00:00`);
    return `${formatDate(`${range.desde}T00:00:00`)} → ${formatDate(`${range.hasta}T00:00:00`)}`;
  }, [range]);

  const onGenerate = async (e) => {
    e.preventDefault();
    if (!holding?._id) {
      showToast(t("No se pudo identificar el holding del usuario."), false);
      return;
    }
    if (!rangeValid) {
      showToast(t("La fecha Fin no puede ser menor que Inicio."), false);
      return;
    }

    setLoading(true);
    try {
      const result = await generateHoldingPackagesReport(
        holding._id,
        range.desde,
        range.hasta,
      );
      if (result?.error) {
        showToast(apiBodyMessage(result.error) || t("No se pudo generar el informe."), false);
        return;
      }
      // 200 with no rows: nothing was downloaded, so it is a warning, not a success.
      if (result?.empty) {
        showToast(t("No hay paquetes en el rango seleccionado."), "warning");
        return;
      }
      showToast(t("Informe generado correctamente."), true);
    } catch (err) {
      console.error("Error generando informe holding:", err);
      showToast(t("Error al generar el informe."), false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-w-0 p-6 space-y-5">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold text-fg flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-fg-muted" />
          {t("Crear informe")}
        </h1>
        <p className="text-sm text-fg-muted">
          {t("Generá reportes consolidados de los paquetes operados por las empresas de tu holding.")}
        </p>
      </header>

      <form
        onSubmit={onGenerate}
        className="rounded-lg border border-border bg-bg overflow-hidden"
      >
        {/* Card header */}
        <div className="flex items-start gap-3 p-5 border-b border-border bg-bg-muted/40">
          <div className="shrink-0 w-10 h-10 rounded-lg bg-brand-50 text-brand-700 inline-flex items-center justify-center">
            <FileSpreadsheet className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-fg">
                {t("Informe consolidado de paquetes")}
              </h2>
              {holding?.name && <Badge tone="brand">{holding.name}</Badge>}
            </div>
            <p className="text-xs text-fg-muted mt-1">
              {t("Excel con todos los paquetes transaccionados por las empresas hijas del holding en el rango seleccionado.")}
            </p>
          </div>
        </div>

        <div className="p-5 space-y-5">
          {/* Presets (chips rápidos + rango personalizado en el popover) */}
          <div className="space-y-2">
            <label className="text-[11px] font-semibold text-fg-muted uppercase tracking-wider flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {t("Rango de fechas")}
            </label>
            <DateRangeFilter
              compact
              value={filterValue}
              onChange={handleRangeChange}
              presets={INFORM_PRESETS}
              customRange={
                range?.desde && range?.hasta
                  ? { start: range.desde, end: range.hasta }
                  : null
              }
            />
          </div>

          {/* Range preview */}
          <div className="rounded-lg border border-border bg-bg-muted/40 p-3">
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs text-fg-muted">{t("Período a exportar")}</span>
              <span className="text-sm font-medium text-fg font-mono">
                {rangeLabel}
              </span>
            </div>
            {!rangeValid && (
              <p className="text-xs text-status-error mt-2 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                {t('La fecha "Hasta" no puede ser anterior a "Desde".')}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 flex-wrap pt-1">
            <p className="text-xs text-fg-muted">
              {t("El archivo se descargará automáticamente al estar listo.")}
            </p>
            <Button
              type="submit"
              loading={loading}
              disabled={loading || !holding?._id || !rangeValid}
              iconLeft={!loading ? <Download className="w-4 h-4" /> : undefined}
            >
              {loading ? t("Generando...") : t("Descargar Excel")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}
